import { type ThemeOptions } from '@mui/material/styles';

export const lightThemeOptions = {
  components: {
    MuiAlert: {
      // v9 removed combined variant+color slots (filledError, etc.); express them via `variants`. Every Alert in the app uses `severity` (not `color`).
      variants: [
        { props: { variant: 'filled', severity: 'error' }, style: { backgroundColor: '#D32F2F', color: '#FFFFFF' } },
        { props: { variant: 'filled', severity: 'info' }, style: { backgroundColor: '#0288D1', color: '#FFFFFF' } },
        { props: { variant: 'filled', severity: 'success' }, style: { backgroundColor: '#00897B', color: '#FFFFFF' } },
        { props: { variant: 'filled', severity: 'warning' }, style: { backgroundColor: '#EF6C00', color: '#FFFFFF' } },
        { props: { variant: 'outlined', severity: 'error' }, style: { borderColor: '#D32F2F', color: '#5F2120' } },
        { props: { variant: 'outlined', severity: 'info' }, style: { borderColor: '#0288D1', color: '#014361' } },
        { props: { variant: 'outlined', severity: 'success' }, style: { borderColor: '#1F978B', color: '#1E4641' } },
        { props: { variant: 'outlined', severity: 'warning' }, style: { borderColor: '#EF6C00', color: '#663C00' } },
        { props: { variant: 'standard', severity: 'error' }, style: { backgroundColor: '#FDEDED', color: '#5F2120' } },
        { props: { variant: 'standard', severity: 'info' }, style: { backgroundColor: '#E5F6FD', color: '#014361' } },
        {
          props: { variant: 'standard', severity: 'success' },
          style: { backgroundColor: '#EDF7F5', color: '#1E4641' },
        },
        {
          props: { variant: 'standard', severity: 'warning' },
          style: { backgroundColor: '#FFF4E5', color: '#663C00' },
        },
      ],
    },
  },
  palette: {
    action: {
      active: 'rgba(11, 15, 25, 0.56)',
      disabled: 'rgba(11, 15, 25, 0.38)',
      disabledBackground: 'rgba(11, 15, 25, 0.12)',
      focus: 'rgba(11, 15, 25, 0.12)',
      hover: 'rgba(11, 15, 25, 0.04)',
      selected: 'rgba(11, 15, 25, 0.08)',
    },
    background: {
      default: '#F7F8FA',
      'paper-elevation-0': '#F7F8FA',
      'paper-elevation-1': '#FFFFFF',
      'paper-elevation-2': '#FFFFFF',
      'paper-elevation-3': '#FFFFFF',
      'paper-elevation-4': '#FFFFFF',
      'paper-elevation-5': '#FFFFFF',
      'paper-elevation-6': '#FFFFFF',
      'paper-elevation-7': '#FFFFFF',
      'paper-elevation-8': '#FFFFFF',
      'paper-elevation-9': '#FFFFFF',
      'paper-elevation-10': '#FFFFFF',
      'paper-elevation-11': '#FFFFFF',
      'paper-elevation-12': '#FFFFFF',
      'paper-elevation-13': '#FFFFFF',
      'paper-elevation-14': '#FFFFFF',
      'paper-elevation-15': '#FFFFFF',
      'paper-elevation-16': '#FFFFFF',
      'paper-elevation-17': '#FFFFFF',
      'paper-elevation-18': '#FFFFFF',
      'paper-elevation-19': '#FFFFFF',
      'paper-elevation-20': '#FFFFFF',
      'paper-elevation-21': '#FFFFFF',
      'paper-elevation-22': '#FFFFFF',
      'paper-elevation-23': '#FFFFFF',
      'paper-elevation-24': '#FFFFFF',
    },
    common: {
      black_states: {
        hover: 'rgba(11, 15, 25, 0.04)',
        selected: 'rgba(11, 15, 25, 0.08)',
        focus: 'rgba(11, 15, 25, 0.12)',
        focusVisible: 'rgba(11, 15, 25, 0.3)',
        outlinedBorder: 'rgba(11, 15, 25, 0.5)',
      },
      white_states: {
        focus: 'rgba(252, 253, 255, 0.12)',
        focusVisible: 'rgba(252, 253, 255, 0.3)',
        hover: 'rgba(252, 253, 255, 0.04)',
        main: '#FFFFFF',
        outlinedBorder: 'rgba(252, 253, 255, 0.5)',
        selected: 'rgba(252, 253, 255, 0.08)',
      },
    },
    error: {
      contrastText: '#FCFDFF',
      main: '#D32F2F',
      _states: {
        hover: 'rgba(211, 47, 47, 0.04)',
        selected: 'rgba(211, 47, 47, 0.08)',
        focus: 'rgba(211, 47, 47, 0.12)',
        focusVisible: 'rgba(211, 47, 47, 0.3)',
        outlinedBorder: 'rgba(211, 47, 47, 0.5)',
      },
    },
    info: {
      contrastText: '#FCFDFF',
      main: '#0288D1',
      _states: {
        hover: 'rgba(2, 136, 209, 0.04)',
        selected: 'rgba(2, 136, 209, 0.08)',
        focus: 'rgba(2, 136, 209, 0.12)',
        focusVisible: 'rgba(2, 136, 209, 0.3)',
        outlinedBorder: 'rgba(2, 136, 209, 0.5)',
      },
    },
    primary: {
      main: '#6047FF',
      dark: '#5F36F5',
      light: '#7E75FF',
      contrastText: '#FCFDFF',
      _states: {
        hover: 'rgba(96, 71, 255, 0.04)',
        selected: 'rgba(96, 71, 255, 0.08)',
        focus: 'rgba(96, 71, 255, 0.12)',
        focusVisible: 'rgba(96, 71, 255, 0.3)',
        outlinedBorder: 'rgba(96, 71, 255, 0.5)',
      },
    },
    secondary: {
      main: '#2196F3',
      dark: '#1976D2',
      light: '#64B5F6',
      contrastText: '#FCFDFF',
      _states: {
        hover: 'rgba(33, 150, 243, 0.04)',
        selected: 'rgba(33, 150, 243, 0.08)',
        focus: 'rgba(33, 150, 243, 0.12)',
        focusVisible: 'rgba(33, 150, 243, 0.3)',
        outlinedBorder: 'rgba(33, 150, 243, 0.5)',
      },
    },
    // Serverless spaces, told apart from the primary-coloured dedicated clusters.
    serverless: {
      main: '#9C27B0',
      _states: {
        selected: 'rgba(156, 39, 176, 0.08)',
      },
    },
    success: {
      main: '#00897B',
      dark: '#004D40',
      light: '#009688',
      contrastText: '#FCFDFF',
      _states: {
        hover: 'rgba(0, 137, 123, 0.04)',
        selected: 'rgba(0, 137, 123, 0.08)',
        focus: 'rgba(0, 137, 123, 0.12)',
        focusVisible: 'rgba(0, 137, 123, 0.3)',
        outlinedBorder: 'rgba(0, 137, 123, 0.5)',
      },
    },
    text: {
      primary: '#111824',
      secondary: '#656B7F',
      disabled: '#ABB1C7',
      _states: {
        hover: 'rgba(17, 24, 36, 0.04)',
        selected: 'rgba(17, 24, 36, 0.08)',
        focus: 'rgba(17, 24, 36, 0.12)',
        focusVisible: 'rgba(17, 24, 36, 0.3)',
      },
    },
    warning: {
      contrastText: '#FCFDFF',
      main: '#EF6C00',
      dark: '#E65100',
      _states: {
        hover: 'rgba(239, 108, 0, 0.04)',
        selected: 'rgba(239, 108, 0, 0.08)',
        focus: 'rgba(239, 108, 0, 0.12)',
        focusVisible: 'rgba(239, 108, 0, 0.3)',
        outlinedBorder: 'rgba(239, 108, 0, 0.5)',
      },
    },
  },
} satisfies ThemeOptions;
